import { Link } from "react-router-dom";
import { PrimaryCTA } from "@/components/shared/CTA";
import { Section, StatusTag, statusTone } from "@/components/shared/Primitives";
import { track, EVENTS } from "@/lib/analytics";

export const CaseStudyHero = ({ project, isConcept }) => (
  <Section testId="case-study-hero" className="!pt-36 !pb-12">
    <div className="flex flex-wrap items-center gap-3">
      <StatusTag tone={statusTone(project.status)}>{project.status}</StatusTag>
      <Link
        to={`/capabilities/${project.capabilitySlug}`}
        data-testid="case-study-capability-link"
        className="rounded-full border border-white/10 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.28em] text-[#06B6D4] transition-colors duration-300 hover:border-[#06B6D4]/50"
      >
        {project.capability}
      </Link>
      <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#F4F6FB]/35">
        {project.year}
      </span>
    </div>

    <h1
      data-testid="case-study-title"
      className="font-display mt-8 max-w-4xl bg-gradient-to-r from-[#F4F6FB] via-[#c9b8f5] to-[#7dd8ea] bg-clip-text text-4xl font-black uppercase leading-[1.02] tracking-tight text-transparent md:text-7xl"
    >
      {project.title}
    </h1>
    <p className="mt-8 max-w-2xl text-lg leading-relaxed text-[#F4F6FB]/80 md:text-2xl">
      {project.intro}
    </p>

    {isConcept && (
      <p
        data-testid="case-study-concept-notice"
        className="mt-8 max-w-2xl rounded-xl border border-[#D4AF37]/25 bg-[#D4AF37]/5 px-5 py-4 font-mono text-[10px] uppercase leading-relaxed tracking-[0.22em] text-[#D4AF37]"
      >
        Concept / self-initiated — this project was created by Elentara as independent research and
        was not produced for a client.
      </p>
    )}

    <div className="mt-14 overflow-hidden rounded-2xl border border-white/10 shadow-[0_24px_70px_rgba(0,0,0,0.55)]">
      <img
        src={project.image}
        alt={project.alt}
        loading="eager"
        decoding="async"
        className="h-72 w-full object-cover md:h-[32rem]"
      />
    </div>
  </Section>
);

export const CaseStudyLive = ({ project, isConcept }) => (
  <div data-testid="case-study-live" className="mt-4 border-t border-white/10 py-14 md:py-16">
    <div className="grid gap-6 md:grid-cols-[6rem_1fr] md:gap-12">
      <span className="font-mono text-xs tracking-[0.3em] text-[#D4AF37]">09</span>
      <div>
        <h2 className="font-display text-xl font-semibold uppercase tracking-[0.12em] text-[#F4F6FB] md:text-2xl">
          Live Experience / Prototype
        </h2>
        {project.liveUrl ? (
          <div className="mt-6">
            <PrimaryCTA
              href={project.liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              testId="case-study-enter-experience"
              onClick={() => track(EVENTS.ENTER_EXPERIENCE_CLICK, { project: project.slug })}
            >
              Enter Experience
            </PrimaryCTA>
          </div>
        ) : (
          <p
            data-testid="case-study-live-empty"
            className="mt-6 max-w-2xl text-sm leading-relaxed text-[#F4F6FB]/50 md:text-base"
          >
            {isConcept
              ? "No public prototype is available for this concept yet. The system architecture is documented above and can be built on request."
              : "This project is not publicly hosted. A walkthrough can be arranged on request."}
          </p>
        )}
      </div>
    </div>
  </div>
);
